import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload);
    if (payload.exp && Date.now() >= (payload.exp - 10) * 1000) {
      return true;
    }
    return false;
  } catch (e) {
    return true;
  }
};

export const useAuth = () => {
  const navigate = useNavigate();
  const [authToken, setAuthToken] = useState(null);
  const [user, setUser] = useState(null); // { name, email, picture }
  const [authError, setAuthError] = useState(null);
  const [errorBanner, setErrorBanner] = useState(null); // { message, retryFn }
  const [toast, setToast] = useState(null); // { message, type }
  const [initializing, setInitializing] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const hasToken = !!localStorage.getItem("auth_token");
      return !!code || hasToken;
    } catch {
      return false;
    }
  });

  // ── Auth: handle Google OAuth redirect (OTC exchange, not token-in-URL) ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code"); // one-time-code (NOT a JWT)
    const error = params.get("error");

    if (code) {
      // Exchange the one-time code for the actual JWT — never stored in URL
      window.history.replaceState({}, document.title, window.location.pathname); // clean URL immediately
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout

      const fetchWithRetry = async (attempt = 1) => {
        try {
          return await fetch("/auth/exchange", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code }),
            signal: controller.signal
          });
        } catch (err) {
          if (attempt < 2 && err.name !== 'AbortError') {
            console.warn(`[auth] OTC exchange failed, retrying in 1.5s (attempt ${attempt})...`);
            await new Promise(r => setTimeout(r, 1500));
            return fetchWithRetry(attempt + 1);
          }
          throw err;
        }
      };

      fetchWithRetry()
        .then((res) => {
          clearTimeout(timeoutId);
          return res.json();
        })
        .then((data) => {
          if (data.token) {
            localStorage.setItem("auth_token", data.token);
            localStorage.setItem("user_name", data.name || "");
            localStorage.setItem("user_email", data.email || "");
            localStorage.setItem("user_picture", data.picture || "");
            setAuthToken(data.token);
            setUser({ name: data.name, email: data.email, picture: data.picture });

            // Restore deep link URL if saved, then redirect
            const intendedRedirect = localStorage.getItem('mmr_redirect_after_login') || '/app/dashboard';
            localStorage.removeItem('mmr_redirect_after_login');
            navigate(intendedRedirect, { replace: true });
          } else {
            setAuthError(data.error || "Login failed. Please try again.");
          }
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          if (err.name === 'AbortError') {
            setAuthError("Google session verification timed out. Please check your connection and try again.");
          } else {
            setAuthError("Login failed due to a slow or interrupted network. Please check your internet connection and try again.");
          }
        })
        .finally(() => {
          setInitializing(false);
        });
    } else if (error) {
      setAuthError("Google login failed. Please try again.");
      window.history.replaceState({}, document.title, "/");
      setInitializing(false);
    } else {
      // Restore existing session from localStorage — but validate first
      const savedToken = localStorage.getItem("auth_token");
      if (savedToken) {
        if (isTokenExpired(savedToken)) {
          console.warn("[auth] Saved token locally expired — clearing session");
          localStorage.removeItem("auth_token");
          localStorage.removeItem("user_name");
          localStorage.removeItem("user_email");
          localStorage.removeItem("user_picture");
          setInitializing(false);
          return;
        }

        // Proactively validate the token before trusting it
        fetch("/auth/me", {
          headers: { Authorization: `Bearer ${savedToken}` },
        })
          .then(async (res) => {
            if (res.ok) {
              const data = await res.json();
              if (data.authenticated) {
                // Token still valid — restore session
                setAuthToken(savedToken);
                setUser({
                  name: localStorage.getItem("user_name") || "",
                  email: localStorage.getItem("user_email") || "",
                  picture: localStorage.getItem("user_picture") || "",
                });
                return;
              } else {
                // Token expired or invalid — clear auth so user sees login
                console.warn("[auth] Saved token invalid/expired (status:", res.status, ") — clearing session");
                localStorage.removeItem("auth_token");
                localStorage.removeItem("user_name");
                localStorage.removeItem("user_email");
                localStorage.removeItem("user_picture");
              }
            } else {
              // Server rejected (e.g. 500, 401, 502)
              if (res.status === 401 || res.status === 403) {
                console.warn("[auth] Server rejected token as unauthorized:", res.status, "— clearing session");
                localStorage.removeItem("auth_token");
                localStorage.removeItem("user_name");
                localStorage.removeItem("user_email");
                localStorage.removeItem("user_picture");
              } else {
                // Transient server error (e.g. 502/503/504) — do NOT log out the user.
                // Restore session locally if the token is not locally expired.
                console.warn("[auth] Transient server error during boot:", res.status, "— keeping local session");
                if (!isTokenExpired(savedToken)) {
                  setAuthToken(savedToken);
                  setUser({
                    name: localStorage.getItem("user_name") || "",
                    email: localStorage.getItem("user_email") || "",
                    picture: localStorage.getItem("user_picture") || "",
                  });
                }
              }
            }
          })
          .catch(() => {
            // Network error — check local expiration first before restoring
            if (isTokenExpired(savedToken)) {
              console.warn("[auth] Network error and token is locally expired — clearing session");
              localStorage.removeItem("auth_token");
              localStorage.removeItem("user_name");
              localStorage.removeItem("user_email");
              localStorage.removeItem("user_picture");
              return;
            }
            // Offline mode / backend rebooting: restore session only if token is locally valid
            setAuthToken(savedToken);
            setUser({
              name: localStorage.getItem("user_name") || "",
              email: localStorage.getItem("user_email") || "",
              picture: localStorage.getItem("user_picture") || "",
            });
          })
          .finally(() => {
            setInitializing(false);
          });
      } else {
        setInitializing(false);
      }
    }
  }, [navigate]);

  // ── Auth helper: build headers for all API calls ──
  const getAuthHeaders = useCallback(() => {
    return authToken ? { Authorization: `Bearer ${authToken}` } : {};
  }, [authToken]);

  // ── Global API fetch wrapper: auto-logout on 401 ──────────────────────────
  const apiFetch = useCallback(async (url, options = {}) => {
    if (authToken && isTokenExpired(authToken)) {
      console.warn('[auth] Local token expiration detected before fetch — logging out');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_name');
      localStorage.removeItem('user_email');
      localStorage.removeItem('user_picture');
      setAuthToken(null);
      setUser(null);
      return new Response(JSON.stringify({ error: 'token_expired', code: 'TOKEN_EXPIRED' }), {
        status: 401,
        headers: { 'Content-Type': 'application/json' }
      });
    }

    const defaultHeaders = {};
    if (options.body && typeof options.body === 'string') {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    const res = await fetch(url, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...(options.headers || {}),
      },
    });
    if (res.status === 401) {
      // Token expired or invalid — force logout immediately
      try {
        const body = await res.json();
        const isExpired = body?.code === 'TOKEN_EXPIRED' || body?.error === 'token_expired';
        console.warn('[auth] 401 received, code:', body?.code, '| isExpired:', isExpired);
      } catch (_) { /* ignore parse error */ }
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_name');
      localStorage.removeItem('user_email');
      localStorage.removeItem('user_picture');
      setAuthToken(null);
      setUser(null);
      return res;
    }
    return res;
  }, [authToken]);

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_email");
    localStorage.removeItem("user_picture");
    localStorage.removeItem("reply");
    setAuthToken(null);
    setUser(null);
  };

  // Redirect logic removed to establish routing table in App.js as the single source of truth.

  return {
    authToken,
    setAuthToken,
    user,
    setUser,
    authError,
    setAuthError,
    errorBanner,
    setErrorBanner,
    toast,
    setToast,
    getAuthHeaders,
    apiFetch,
    handleLogout,
    initializing,
  };
};
